import { Auth } from 'aws-amplify';
import axios from 'axios';
import { getDashboardOrgHeader } from 'src/utils/dashboard-org-header';

/**
 * National dashboard API client (/national).
 *
 * Talks to the pg-api national service, which reads the precomputed
 * `national_voter_rollup` cube. Superadmin-only on the server side.
 *
 * EVERY response echoes the `basis` it was computed under. The election
 * context (general / midterm / primary) changes what "likely voter" means, so
 * two panels rendered on different bases silently disagree. Callers should
 * pass the echoed basis through to the UI rather than assuming the one they
 * asked for came back.
 */

const BASE = () => `${process.env.REACT_APP_POSTGRES_API_URL}/api/national`;

const authHeaders = async () => {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-selected-organization': getDashboardOrgHeader(),
  };
};

const get = async (path, params) => {
  const headers = await authHeaders();
  const response = await axios.get(`${BASE()}${path}`, { headers, params });
  return response?.data?.data ?? null;
};

const post = async (path, body) => {
  const headers = await authHeaders();
  const response = await axios.post(`${BASE()}${path}`, body, { headers });
  return response?.data?.data ?? null;
};

// ---------------------------------------------------------------------------
// Election context
// ---------------------------------------------------------------------------

export const BASIS = {
  GENERAL: 'general',
  MIDTERM: 'midterm',
  PRIMARY: 'primary',
  PRESIDENTIAL_ONLY: 'presidential_only',
  MIDTERM_ONLY: 'midterm_only',
};

export const BASIS_LABELS = {
  general: 'General',
  midterm: 'Midterm',
  primary: 'Primary',
  presidential_only: 'Presidential-only voters',
  midterm_only: 'Midterm-only voters',
};

/**
 * ⚑ A BASIS IS ONE OF TWO DIFFERENT THINGS, and the difference decides what the
 * numbers on the page mean.
 *
 * A WINDOW says which elections we measure and keeps every voter. A voter is
 * "likely" when they voted in at least half of that window:
 *
 *   general : 2018 · 2020 · 2022 · 2024  -> 2 of 4  (95.7M likely of 158.7M)
 *   midterm : 2018 · 2022                -> 1 of 2  (87.0M likely)
 *
 * A COHORT says which voters we keep — people who turn out for one kind of
 * general election and sit out the other. Selecting one narrows EVERY number on
 * the page, `registered` included: it is an audience, not a lens.
 *
 *   presidential_only : voted 2020 and/or 2024, never 2018/2022
 *   midterm_only      : voted 2018 and/or 2022, never 2020/2024
 *
 * ⚠️ On a cohort, LIKELY EQUALS THE WHOLE COHORT and turnout is 100% on every
 * unit. Membership requires a ballot in the window and the likely bar is 1 of 2,
 * so the two tests are the same predicate (Daniel's spec, raised and confirmed
 * 2026-08-04). Show `electorate` — the unit's full population, which the server
 * returns only on a cohort — as a SHARE instead of a turnout rate.
 *
 * ⚠️ `midterm` the WINDOW and `midterm_only` the COHORT are different by roughly
 * a factor of thirty. Measured in WV: 629,565 voters are likely on the midterm
 * window and 605,004 of them — 96% — are also likely on the general window, so
 * only 24,561 were midterm-but-not-general. Never treat one as shorthand for the
 * other, and never let a label say only "Midterm".
 *
 * PRIMARY stays valid server-side but is not offered: this screen is about
 * general elections (user, 2026-08-03).
 *
 * ⚠️ The windows mirror the ELECTION CALENDAR block in
 * build-national-geo-rollup.sql, which is the definition. When 2026 lands and
 * they shift, this comment shifts with them — and note 2026 is a midterm, so it
 * enters both cohort membership tests as well.
 */
export const ELECTION_BASES = [
  { key: BASIS.GENERAL, label: 'All general elections' },
  { key: BASIS.PRESIDENTIAL_ONLY, label: 'Presidential years only' },
  { key: BASIS.MIDTERM_ONLY, label: 'Midterm years only' },
];

/** Bases that cover a subset of the electorate. Mirrors COHORT_BASES server-side. */
const COHORT_BASES = [BASIS.PRESIDENTIAL_ONLY, BASIS.MIDTERM_ONLY];

export const isCohortBasis = (basis) => COHORT_BASES.includes(basis);

/**
 * Which basis to open on.
 *
 * ⚠️ ALWAYS `general`, at every year — including midterm years, which is a
 * change. The old rule opened 2026 on the `midterm` WINDOW, reasoning that the
 * 2026 electorate looks like 2018/2022 rather than the presidential one. That
 * reasoning still holds, but the window is no longer in the picker: the three
 * options are now Daniel's, and `midterm_only` is a cohort thirty times smaller
 * than the window it shares a word with. Defaulting to a basis the dropdown
 * cannot show would leave the control reading as something it is not.
 *
 * Neither cohort should ever be a default in any case — they are targeting
 * views, and most people work on all general elections (user, 2026-08-04).
 *
 * Exported so the rule is testable and readable rather than inferred.
 */
export const defaultElectionBasis = () => BASIS.GENERAL;

/**
 * The five modelled-party buckets, spelled exactly as `calcparty` stores them.
 * Kept identical to CALCPARTY in src/api/geographical-overview.js on purpose —
 * two spellings of the same bucket is the drift this codebase has been bitten
 * by before.
 */
export const SEGMENTS = [
  { key: 'Solid Republican', label: 'Solid Republican', color: '#b01e40' },
  { key: 'Soft Republican', label: 'Soft Republican', color: '#e87d6e' },
  { key: 'Swing / Unaffiliated', label: 'Unaffiliated', color: '#4b515a' },
  { key: 'Soft Democrat', label: 'Soft Democrat', color: '#8fb8e0' },
  { key: 'Solid Democrat', label: 'Strong Democrat', color: '#2f6ea8' },
];

/**
 * Format a signed margin the way every screen in this section shows it:
 * positive is Republican, because the product is built from the Republican
 * party's point of view.
 *
 * A NULL margin is NOT zero. It means the unit has no voter with a modelled
 * party at all (Kentucky has 3.3M such voters), and rendering it as "Even"
 * would assert a finding we do not have.
 */
export const formatMargin = (marginR) => {
  if (marginR === null || marginR === undefined) return 'No data';
  // Whole points, no decimals (user decision 2026-08-03). Anything that rounds
  // to zero reads "Even" rather than "R+0", which would claim a direction the
  // rounding has just thrown away.
  const value = Math.abs(Math.round(marginR));
  if (value === 0) return 'Even';
  return `${marginR > 0 ? 'R' : 'D'}+${value}`;
};

export const marginColor = (marginR) => {
  if (marginR === null || marginR === undefined) return '#9aa0ab';
  if (Math.abs(marginR) <= 3) return '#b7791f';
  return marginR > 0 ? '#c0392b' : '#2a6fb0';
};

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * Which states are loaded and how good their data is.
 *
 * A state ABSENT from `states` is not loaded, and must render differently from
 * a state with zero voters — otherwise a 37-state total reads as national.
 * `statesWithoutCalcParty` and `statesWithoutTurnoutHistory` name the states
 * whose composition bars and turnout numbers respectively cannot be trusted.
 */
export const fetchCoverage = () => get('/coverage');

/**
 * Pre-projected SVG path bundle. level = 'state' | 'congressional'.
 *
 * National bundles carry `state = ''` — the same convention the cube uses for
 * cross-state rows. Pass a state only if a per-state bundle is ever built.
 */
/**
 * The five political regions and their member states.
 *
 * Served rather than hardcoded because `national_regions` is what the cube
 * joins to when it resolves a region's children — a copy here would be free to
 * disagree with the numbers rendered next to it. Region codes also collide
 * with state codes (region NE is the Northeast, state NE is Nebraska), so the
 * membership list is the only safe way to answer "which region is this state
 * in".
 */
export const fetchRegions = () => get('/regions');

export const fetchBoundaries = (level, state = '') => get('/boundaries', { level, state });

/**
 * Every cube response echoes the basis it was computed under. If that is not
 * what we asked for, the numbers describe a different likely-voter definition
 * than the caller is about to label them with — 87.0M voters nationally on
 * midterm against 95.7M on general, both entirely plausible on screen. Fail
 * loudly instead, the same guard fetchGeoTable applies to childLevel.
 */
const assertBasis = (data, requested) => {
  if (requested && data?.basis && data.basis !== requested) {
    throw new Error(`Expected '${requested}' basis but the API returned '${data.basis}'.`);
  }
  return data;
};

/** KPI strip + composition for one unit. */
export const fetchOverview = async ({ level, geoKey, basis = BASIS.GENERAL, partyBasis }) =>
  assertBasis(await post('/overview', { level, geoKey, basis, partyBasis }), basis);

/** One breakdown dimension's groups for a unit. */
export const fetchBreakdown = async ({ level, geoKey, basis = BASIS.GENERAL, dimension }) =>
  assertBasis(await post('/breakdown', { level, geoKey, basis, dimension }), basis);

/**
 * Ranked child units of a scope.
 *
 * ⚠️ PASS `childLevel` EXPLICITLY when the caller needs a specific one. Each
 * scope has a *default* child (nation → region), and omitting the parameter
 * silently gets you that default rather than an error.
 *
 * That is not a theoretical concern. The map at nation scope draws STATES, so
 * dropping `childLevel: 'state'` returned REGION rows keyed MTW / MW / NE /
 * PACW / SE — and because the Northeast region's code `NE` collides with
 * Nebraska's state code, the map shaded exactly one state: Nebraska, with the
 * Northeast's numbers. Everything else fell through as "no data". A wrong
 * answer that renders cleanly, which is the failure mode this whole API tries
 * to avoid.
 *
 * `unassigned` is the parent's voters who fall in no child unit (blank or
 * non-numeric district). Sub-state levels deliberately do not sum to their
 * parent, so this must be shown rather than letting the rows quietly not add
 * up.
 */
export const fetchGeoTable = async ({
  level,
  geoKey,
  basis = BASIS.GENERAL,
  childLevel,
  partyBasis,
}) => {
  const data = assertBasis(
    await post('/geo-table', { level, geoKey, basis, childLevel, partyBasis }),
    basis
  );

  // The endpoint echoes the childLevel it actually used. If that is not what we
  // asked for, the rows describe different geography than the caller is about
  // to render them as — fail loudly rather than draw them.
  if (childLevel && data?.childLevel && data.childLevel !== childLevel) {
    throw new Error(
      `geo-table returned '${data.childLevel}' rows but '${childLevel}' was requested.`
    );
  }
  return data;
};

/**
 * How the electorate has changed since the last federal election, from the precomputed
 * registration-momentum rollup.
 *
 * ⚠️ The party-change figures are NOT in-state switching. The voter file records a previous party
 * only for voters who moved in from another state — verified across ID, NC and WV, where every
 * such record also carries a previous state and there are zero exceptions. This is partisan change
 * through migration; the UI must never label it "party switching".
 */
export const fetchRegistrationMomentum = ({ level, geoKey }) =>
  post('/registration-momentum', { level, geoKey });

/**
 * Who moved into this scope since the window start, from where, and how they registered.
 *
 * ⚠️ Carries no "moved but not yet registered" figure. Such a person is absent from the voter file
 * by definition, so it cannot be counted — the demo's number for it is fabricated.
 */
export const fetchMigrationFlow = ({ level, geoKey }) =>
  post('/migration-flow', { level, geoKey });

/**
 * Which states are gaining and losing registrations to other states. Unscoped — always the
 * cross-state table, sorted client-side for the in/out views.
 *
 * ⚠️ Every figure is a FLOOR, and the ranking partly tracks how diligently a state records a
 * previous state rather than how many people moved. Both directions cover only moves between
 * loaded states, so they share one universe and the national net is 0.
 */
export const fetchMigrationBalance = () => get('/migration-balance');

/**
 * One state's counterparties behind a balance row: where its arrivals came from (`in`) or where
 * its leavers went (`out`). Restricted to loaded states like the table, so the rows sum exactly to
 * the cell that opened them.
 */
export const fetchMigrationDrill = ({ state, direction }) =>
  get('/migration-drill', { state, direction });

/**
 * How ballots were cast in a past general — election day, absentee/mail, early in person.
 *
 * ⚠️ OBSERVED, not a forecast. The card is titled "Expected vote method" per the design reference,
 * and its subtitle says the mix is modelled from that election — which is what it is. The demo's
 * own numbers are a hash.
 */
/**
 * The vote-method card is a breakdown like any other — the cube gained `vote_method_g2024` as a
 * ninth dim, so this is `fetchBreakdown` with that dimension rather than an endpoint of its own.
 */
export const VOTE_METHOD_DIMENSION = 'vote_method_g2024';

/**
 * The dimensions the Breakdown by group tabs offer — the demo's eight, exactly
 * (NationalTracking.jsx:3808).
 *
 * ⚠️ `vote_method_g2024` is deliberately NOT here. It is a real cube dimension and `/breakdown`
 * serves it, but it has its own card, and offering it in both places would ask the same question
 * twice with different framing — the card shows shares of BALLOTS, a tab here would show shares of
 * registered voters including `did_not_vote`. One home per question.
 *
 * `calc_party` is a PSEUDO-dimension — it lives in the cube's grain, not as a `dim` value, and the
 * server special-cases it. Included because the reference lists "Inferred party" as a breakdown and
 * it would be odd to omit the axis this whole dashboard is built around.
 */
export const BREAKDOWN_DIMENSIONS = [
  { key: 'reg_party', label: 'Registered party' },
  { key: 'calc_party', label: 'Inferred party' },
  { key: 'age_range', label: 'Age' },
  { key: 'income', label: 'Household income' },
  { key: 'education', label: 'Education' },
  { key: 'religion', label: 'Religion' },
  { key: 'ethnicity', label: 'Race / ethnicity' },
  { key: 'sex', label: 'Gender' },
];


/**
 * Base supporters / persuadable / low-turnout base, cut from the cube's modelled party x turnout.
 *
 * ⚠️ These do NOT sum to the universe — Democrats are in none of them — so the response carries
 * `unsegmented` and the card must show what it leaves out.
 */
export const fetchVoterSegments = ({ level, geoKey, basis }) =>
  post('/segments', { level, geoKey, basis });

/**
 * Live sub-state drill: state-leg districts, counties, precincts or cities
 * within one state, optionally intersected with a congressional district.
 *
 * Slower than the cube-backed calls — it reads voters directly — so callers
 * should load it after the page has painted rather than blocking on it.
 */
export const fetchUnitDrill = ({
  state,
  basis = BASIS.GENERAL,
  chamber = 'senate',
  unit = 'district',
  withinDistrict = null,
  withinCounty = null,
}) => post('/unit-drill', { state, basis, chamber, unit, withinDistrict, withinCounty });

/**
 * Two-party presidential margin per state for the "Previous Election" map.
 *
 * ⚠️ STATE LEVEL ONLY — `hasDistrictLevel` is false. There is no
 * presidential-by-congressional-district source, so a district cannot be
 * shaded by its own presidential result. If a district view falls back to its
 * state's number it MUST be labelled as statewide: an unlabelled statewide
 * figure on a district reads as exactly the crossover signal it is not.
 */
export const fetchPreviousElection = (year = 2024) => get('/previous-election', { year });

/**
 * Chamber seat standing for the hemicycle panel.
 *
 * The only panel independent of cube coverage — every seat is covered by
 * seat_control + election_two_party, so it renders complete while the map
 * still greys out unloaded states.
 *
 * ⚠️ Check `isCurrentStanding` before labelling. While it is true the buckets
 * come from the last certified result and who holds the seat — that is NOT a
 * projection, and calling it one claims modelling that does not exist yet.
 */
export const fetchSeats = ({ chamber = 'house', state = null, cycle = 2026 } = {}) =>
  get('/seats', { chamber, cycle, ...(state ? { state } : {}) });

/**
 * The committee's priority slate, with each district's cube totals, holder,
 * 2024 margin and rating bucket.
 *
 * ⚠️ `rating` is bucketed from the REAL 2024 two-party margin, not a model.
 * The demo's own board buckets a hash of the district code. Do not label this
 * "projected" — the caption must say what it is, exactly as the seat chart's
 * does.
 *
 * ⚠️ `candidateName` may be NULL, meaning the committee has named nobody in
 * that district. Render it BLANK. Never substitute the best-funded filer:
 * they supply the district and the candidate together as one editorial
 * package, and a plausible wrong name presented as their pick is worse than
 * an empty line.
 *
 * `withoutCubeData` lists districts whose state is not loaded. Those have
 * `totals: null`, which is not zero and must not render as zero.
 */
export const fetchPriorityDistricts = ({ basis = BASIS.GENERAL, cycle = 2026, state = null } = {}) =>
  post('/priority-districts', { basis, cycle, ...(state ? { state } : {}) });

/**
 * Returned ballots by congressional district, for ONE state.
 *
 * ⚠️ Costs ~5.4 s warm — it is the only live voter-partition query in this
 * section. Never call it on first paint, and never for the whole slate: fetch
 * it per state, after the board has painted, and memoise the result.
 *
 * ⚠️ `hasAbsenteeData: false` means the state has no absentee feed at all.
 * That is NOT zero returns. Only a handful of states have a meaningful feed
 * (MI, WI and VA among the priority slate), so this is the common case, and
 * rendering it as 0 would assert that nobody has voted.
 *
 * A district missing from `districts` in a state that DOES have data is a
 * genuine zero.
 */
export const fetchBallotReturns = (state) => post('/ballot-returns', { state });

/**
 * Field & Contact: goal, attempts and actual voter contacts per channel for a scope, plus the same
 * figures for the scope's children (the contacts-by-area grid).
 *
 * ONE call serves both halves of the tab on purpose. The grid renders the scope's CHILDREN while
 * the summary renders the scope ITSELF, and every bug this section has had in that area came from
 * a caller mismatching the two. The response echoes `areaLevel` so nothing has to infer whose rows
 * it is showing, and a scope with no drillable child (a district) comes back with `areaLevel: null`
 * and an empty list rather than a 400 — a 400 there previously took out a whole panel.
 *
 * ⚠️ `built: false` means this scope is absent from the rollup, NOT that nobody campaigned here.
 * A state with no field programme is a real and expected result and must render differently.
 *
 * ⚠️ `goal` counts WORK ITEMS, not distinct voters — one person can sit in several call lists, so
 * it may legitimately exceed a district's electorate. It is planned effort, not intended reach.
 */
export const fetchContactSummary = ({ level, geoKey, childLevel }) =>
  post('/contact-summary', { level, geoKey, childLevel });
